import { PolicyAdminUtils } from '../../../../common/js/policyAdmin/PolicyAdminUtils';
import { ActionTypes } from '../types/Types';
import {
  AnnuitySummaryInfo,
  ApplicationStages,
  POST_ISSUE_STATUS_MAP_KEYS,
  POST_SUBMIT_STATUS_MAP_KEYS,
  PRE_SUBMIT_STATUS_MAP_KEYS,
  REGISTERED_WORKFLOW_STATES,
  REGISTERED_WORKFLOW_SUB_STATES,
  STATUSES,
  SUB_STATUS,
} from './AnnuityStatusTypes';

export type AnnuityStatusMapValue = {
  desc: string;
  subStatus?: ((summary: AnnuitySummaryInfo) => string) | ((summary: AnnuitySummaryInfo) => Promise<string>);
  actions: Array<ActionTypes>;
  stage: ApplicationStages;
};

export type PreSubmitStatusMap = {
  [key: string]: AnnuityStatusMapValue;
};

export type PostSubmitStatusMap = PreSubmitStatusMap;

// The types PreSubmitStatusMap, PostSubmitStatusMap and PostIssueStatusMap have the same definitions, which is why once one of them is defined, the others are just copies of it.
export type PostIssueStatusMap = PreSubmitStatusMap;

export type WorkflowStateToStatusMap = {
  [workflowState: string]: {
    [workflowSubState: string]: POST_SUBMIT_STATUS_MAP_KEYS | POST_ISSUE_STATUS_MAP_KEYS;
  };
};

export const PRE_SUBMIT_STATES = [
  {
    state: PRE_SUBMIT_STATUS_MAP_KEYS.APPLICATION_STARTED,
    match(application: AnnuitySummaryInfo) {
      const {
        applicationFormQsID,
        suitabilityFormQsID,
        fR3013FormQsID,
        replacementFormQsID,
        cotFormQsID,
        workflowState,
        workflowSubState,
      } = application;
      return (
        applicationFormQsID &&
        !suitabilityFormQsID &&
        !fR3013FormQsID &&
        !replacementFormQsID &&
        !cotFormQsID &&
        !workflowState &&
        !workflowSubState
      );
    },
  },
  {
    state: PRE_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_STARTED,
    match(application: AnnuitySummaryInfo) {
      const {
        applicationFormQsID,
        suitabilityFormQsID,
        replacementFormQsID,
        cotFormQsID,
        workflowState,
        workflowSubState,
      } = application;
      return (
        applicationFormQsID &&
        suitabilityFormQsID &&
        !replacementFormQsID &&
        !cotFormQsID &&
        !workflowState &&
        !workflowSubState
      );
    },
  },
  {
    state: PRE_SUBMIT_STATUS_MAP_KEYS.FR3013_STARTED,
    match(application: AnnuitySummaryInfo) {
      const { applicationFormQsID, replacementFormQsID, cotFormQsID, fR3013FormQsID, workflowState, workflowSubState } =
        application;
      return (
        applicationFormQsID &&
        fR3013FormQsID &&
        !replacementFormQsID &&
        !cotFormQsID &&
        !workflowState &&
        !workflowSubState
      );
    },
  },
  {
    state: PRE_SUBMIT_STATUS_MAP_KEYS.REPLACEMENT_STARTED,
    match(application: AnnuitySummaryInfo) {
      const {
        applicationFormQsID,
        suitabilityFormQsID,
        fR3013FormQsID,
        replacementFormQsID,
        cotFormQsID,
        workflowState,
        workflowSubState,
      } = application;
      return (
        applicationFormQsID &&
        (suitabilityFormQsID || fR3013FormQsID) &&
        replacementFormQsID &&
        !cotFormQsID &&
        !workflowState &&
        !workflowSubState
      );
    },
  },
  {
    state: PRE_SUBMIT_STATUS_MAP_KEYS.CERTIFICATE_OF_TRUST_STARTED,
    match(application: AnnuitySummaryInfo) {
      const { cotFormQsID, workflowState, workflowSubState } = application;
      return cotFormQsID && !workflowState && !workflowSubState;
    },
  },
];

export const WORKFLOW_STATE_TO_STATUS_MAP: WorkflowStateToStatusMap = {
  [REGISTERED_WORKFLOW_STATES.WORKFLOW_SETUP]: {
    [REGISTERED_WORKFLOW_SUB_STATES.INITIALIZATION]: POST_SUBMIT_STATUS_MAP_KEYS.OPERATIONS_REVIEW,
    [REGISTERED_WORKFLOW_SUB_STATES.ROUTING]: POST_SUBMIT_STATUS_MAP_KEYS.OPERATIONS_REVIEW,
  },
  [REGISTERED_WORKFLOW_STATES.EDIT_ANNUITY]: {
    [REGISTERED_WORKFLOW_SUB_STATES.EDITING]: POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_EDIT,
  },
  [REGISTERED_WORKFLOW_STATES.CUSTOMER_CONFIRMATION]: {
    [REGISTERED_WORKFLOW_SUB_STATES.WAITING_CUSTOMER_SIGNATURE]: POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_SENT,
  },
  [REGISTERED_WORKFLOW_STATES.ID_CHECK]: {
    [REGISTERED_WORKFLOW_SUB_STATES.VERIFYING]: POST_SUBMIT_STATUS_MAP_KEYS.SUBMITTED,
  },
  [REGISTERED_WORKFLOW_STATES.SUITABILITY]: {
    [REGISTERED_WORKFLOW_SUB_STATES.REVIEWING]: POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REVIEW,
  },
  [REGISTERED_WORKFLOW_STATES.SUITABILITY_APPROVED]: {
    [REGISTERED_WORKFLOW_SUB_STATES.OFFER_READY_TO_SEND]: POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_APPROVED,
  },
  [REGISTERED_WORKFLOW_STATES.SUITABILITY_REJECTED]: {
    [REGISTERED_WORKFLOW_SUB_STATES.SUITABILITY_REJECTED]: POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REJECTED,
  },
  [REGISTERED_WORKFLOW_STATES.OFFER_ACCEPTANCE]: {
    [REGISTERED_WORKFLOW_SUB_STATES.WAITING_ANNUITY_ACCEPTANCE]: POST_SUBMIT_STATUS_MAP_KEYS.APPROVED,
  },
  [REGISTERED_WORKFLOW_STATES.ISSUE]: {
    [REGISTERED_WORKFLOW_SUB_STATES.PROCESSING]: POST_SUBMIT_STATUS_MAP_KEYS.OFFER_ACCEPTED,
  },
  [REGISTERED_WORKFLOW_STATES.CANCEL]: {
    [REGISTERED_WORKFLOW_SUB_STATES.CANCEL]: POST_SUBMIT_STATUS_MAP_KEYS.CANCELLED,
    [REGISTERED_WORKFLOW_SUB_STATES.TIMEOUT]: POST_SUBMIT_STATUS_MAP_KEYS.CANCELLED,
  },
  [REGISTERED_WORKFLOW_STATES.IN_FORCE]: {
    [REGISTERED_WORKFLOW_SUB_STATES.ISSUED]: POST_ISSUE_STATUS_MAP_KEYS.IN_FORCE,
  },
  [REGISTERED_WORKFLOW_STATES.OPERATIONS_REVIEW]: {
    [REGISTERED_WORKFLOW_SUB_STATES.OPERATIONS_REVIEW]: POST_SUBMIT_STATUS_MAP_KEYS.OPERATIONS_REVIEW,
  },
  [REGISTERED_WORKFLOW_STATES.ADDITIONAL_SIGNATURES]: {
    [REGISTERED_WORKFLOW_SUB_STATES.ADDITIONAL_SIGNATURES]: POST_SUBMIT_STATUS_MAP_KEYS.ADDITIONAL_SIGNATURES,
  },
  CATCH_ALL: {
    PRE_ISSUE: POST_SUBMIT_STATUS_MAP_KEYS.SUBMITTED,
    POST_ISSUE: POST_SUBMIT_STATUS_MAP_KEYS.OFFER_ACCEPTED,
  },
};

/**
 * for agent account center application statuses prior to application submission.
 */
export const PRE_SUBMIT_STATUS_MAP: PreSubmitStatusMap = {
  [PRE_SUBMIT_STATUS_MAP_KEYS.APPLICATION_STARTED]: {
    desc: STATUSES[PRE_SUBMIT_STATUS_MAP_KEYS.APPLICATION_STARTED],
    actions: [ActionTypes.EDIT_APPLICATION, ActionTypes.DELETE],
    stage: ApplicationStages.PRE_SUBMIT,
  },
  [PRE_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_STARTED]: {
    desc: STATUSES[PRE_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_STARTED],
    actions: [ActionTypes.EDIT_APPLICATION, ActionTypes.EDIT_SUITABILITY_FORM, ActionTypes.DELETE],
    stage: ApplicationStages.PRE_SUBMIT,
  },
  [PRE_SUBMIT_STATUS_MAP_KEYS.FR3013_STARTED]: {
    desc: STATUSES[PRE_SUBMIT_STATUS_MAP_KEYS.FR3013_STARTED],
    actions: [ActionTypes.EDIT_APPLICATION, ActionTypes.EDIT_FR3013_FORM, ActionTypes.DELETE],
    stage: ApplicationStages.PRE_SUBMIT,
  },
  [PRE_SUBMIT_STATUS_MAP_KEYS.REPLACEMENT_STARTED]: {
    desc: STATUSES[PRE_SUBMIT_STATUS_MAP_KEYS.REPLACEMENT_STARTED],
    actions: [
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.DELETE,
    ],
    stage: ApplicationStages.PRE_SUBMIT,
  },
  [PRE_SUBMIT_STATUS_MAP_KEYS.CERTIFICATE_OF_TRUST_STARTED]: {
    desc: STATUSES[PRE_SUBMIT_STATUS_MAP_KEYS.CERTIFICATE_OF_TRUST_STARTED],
    actions: [
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.EDIT_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.DELETE,
    ],
    stage: ApplicationStages.PRE_SUBMIT,
  },
};

/**
 * for agent account center application statuses after application submission before going into Policy Admin.
 * @docs https://docs.google.com/spreadsheets/d/16G5TlV9RrO7OhNT8iBSHx_kMSpOGuZ2nofEAQCdUfjM/edit#gid=1536912187
 *
 * TODO ActionTypes.COPY_APPLICATION will be added after MVP
 */
export const POST_SUBMIT_STATUS_MAP: PostSubmitStatusMap = {
  [POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_SENT]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_SENT],
    actions: [
      ActionTypes.EDIT_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.SUBMITTED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.SUBMITTED],
    actions: [
      ActionTypes.EDIT_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_EDIT]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.APPLICATION_EDIT],
    actions: [
      ActionTypes.EDIT_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REVIEW]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REVIEW],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_APPROVED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_APPROVED],
    subStatus: (summary: AnnuitySummaryInfo) => {
      return SUB_STATUS.offerReadyToSend;
    },
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REJECTED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.SUITABILITY_REJECTED],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.COPY_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.REJECTED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.REJECTED],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.APPROVED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.APPROVED],
    subStatus: (summary: AnnuitySummaryInfo) => {
      // @TODO how do we figure out if offer has been sent to the client (DB value or workflow)
      return SUB_STATUS.offerSent;
    },
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.CANCEL,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.OFFER_ACCEPTED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.OFFER_ACCEPTED],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.CANCELLED]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.CANCELLED],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.OPERATIONS_REVIEW]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.OPERATIONS_REVIEW],
    actions: [
      ActionTypes.EDIT_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.EDIT_REPLACEMENT_FORM,
      ActionTypes.EDIT_SUITABILITY_FORM,
      ActionTypes.EDIT_FR3013_FORM,
      ActionTypes.EDIT_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
  [POST_SUBMIT_STATUS_MAP_KEYS.ADDITIONAL_SIGNATURES]: {
    desc: STATUSES[POST_SUBMIT_STATUS_MAP_KEYS.ADDITIONAL_SIGNATURES],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
      ActionTypes.GENERATE_ILLUSTRATION_DOC,
    ],
    stage: ApplicationStages.POST_SUBMIT,
  },
};

export const POST_ISSUE_STATUS_MAP: PostIssueStatusMap = {
  [POST_ISSUE_STATUS_MAP_KEYS.IN_FORCE]: {
    desc: STATUSES[POST_ISSUE_STATUS_MAP_KEYS.IN_FORCE],
    actions: [
      ActionTypes.VIEW_CERTIFICATE_OF_TRUST_FORM,
      ActionTypes.VIEW_REPLACEMENT_FORM,
      ActionTypes.VIEW_SUITABILITY_FORM,
      ActionTypes.VIEW_FR3013_FORM,
      ActionTypes.VIEW_APPLICATION,
    ],
    stage: ApplicationStages.POST_ISSUE,
    subStatus: async (annuity: AnnuitySummaryInfo) => {
      const status = await PolicyAdminUtils.getPolicyAdminAnnuityStatus(annuity.policyNumber);
      return status;
    },
  },
};
